import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Support as SupportIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  where,
  doc,
  updateDoc,
  onSnapshot,
  addDoc,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const AdminDashboard = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [salons, setSalons] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    totalSalons: 0,
    activeSalons: 0,
    trialSalons: 0,
    paidSalons: 0,
    openTickets: 0,
    totalRevenue: 0
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { currentUser } = useAuth();

  useEffect(() => {
    fetchSalons();
    fetchTickets();
  }, []);

  const fetchSalons = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'salon'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const salonsData = [];
      
      querySnapshot.forEach((doc) => {
        salonsData.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt)
        });
      });
      
      setSalons(salonsData);
      calculateStats(salonsData);
    } catch (error) {
      console.error('Error fetching salons:', error);
    }
  };

  const fetchTickets = () => {
    const q = query(
      collection(db, 'support_tickets'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const ticketsData = [];
      querySnapshot.forEach((doc) => {
        ticketsData.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt)
        });
      });
      setTickets(ticketsData);
    });

    return unsubscribe;
  };

  const calculateStats = (salonsData) => {
    const now = new Date();
    let activeSalons = 0;
    let trialSalons = 0;
    let paidSalons = 0;

    salonsData.forEach(salon => {
      if (salon.subscription) {
        const endDate = salon.subscription.endDate?.toDate ? 
          salon.subscription.endDate.toDate() : 
          new Date(salon.subscription.endDate);
        
        if (salon.subscription.active && endDate > now) {
          activeSalons++;
          if (salon.subscription.plan === 'trial') {
            trialSalons++;
          } else {
            paidSalons++;
          }
        }
      }
    });

    const openTickets = tickets.filter(ticket => ticket.status === 'open').length;

    setStats({
      totalSalons: salonsData.length,
      activeSalons,
      trialSalons,
      paidSalons,
      openTickets,
      totalRevenue: paidSalons * 35 // Assuming 35€/month
    });
  };

  const getSubscriptionStatus = (salon) => {
    if (!salon.subscription) return { status: 'Aucun', color: 'default' };
    
    const now = new Date();
    const endDate = salon.subscription.endDate?.toDate ? 
      salon.subscription.endDate.toDate() : 
      new Date(salon.subscription.endDate);
    
    if (!salon.subscription.active || endDate <= now) {
      return { status: 'Expiré', color: 'error' };
    }
    
    if (salon.subscription.plan === 'trial') {
      const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return { status: `Essai (${daysLeft}j)`, color: 'warning' };
    }
    
    return { status: 'Payant', color: 'success' };
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const ticketRef = doc(db, 'support_tickets', ticketId);
      await updateDoc(ticketRef, {
        status: status,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  const replyToTicket = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;

    setLoading(true);
    try {
      const ticketRef = doc(db, 'support_tickets', selectedTicket.id);
      
      const newMessage = {
        id: Date.now().toString(),
        sender: 'admin',
        senderName: 'Support BookingBarber',
        message: replyMessage,
        timestamp: new Date()
      };

      await updateDoc(ticketRef, {
        messages: arrayUnion(newMessage),
        status: 'in_progress',
        updatedAt: new Date()
      });

      setReplyMessage('');
      setSelectedTicket(null);
    } catch (error) {
      console.error('Error replying to ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'open': return 'error';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getTicketStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Ouvert';
      case 'in_progress': return 'En cours';
      case 'resolved': return 'Résolu';
      case 'closed': return 'Fermé';
      default: return status;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Administration BookingBarber
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4">{stats.totalSalons}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Salons
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <DashboardIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4">{stats.activeSalons}</Typography>
              <Typography variant="body2" color="text.secondary">
                Salons Actifs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PaymentIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4">{stats.paidSalons}</Typography>
              <Typography variant="body2" color="text.secondary">
                Abonnements Payants
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SupportIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h4">{stats.openTickets}</Typography>
              <Typography variant="body2" color="text.secondary">
                Tickets Ouverts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Salons" />
            <Tab label="Support Tickets" />
            <Tab label="Statistiques" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Salons Tab */}
          {currentTab === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Salon</TableCell>
                    <TableCell>Propriétaire</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Ville</TableCell>
                    <TableCell>Catégorie</TableCell>
                    <TableCell>Abonnement</TableCell>
                    <TableCell>Créé le</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salons.map((salon) => {
                    const subscription = getSubscriptionStatus(salon);
                    return (
                      <TableRow key={salon.id}>
                        <TableCell>{salon.salonName}</TableCell>
                        <TableCell>{salon.ownerName}</TableCell>
                        <TableCell>{salon.email}</TableCell>
                        <TableCell>{salon.city}</TableCell>
                        <TableCell>{salon.category}</TableCell>
                        <TableCell>
                          <Chip 
                            onClick={() => {}}
                            label={subscription.status}
                            color={subscription.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {dayjs(salon.createdAt).format('DD/MM/YYYY')}
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small"
                            onClick={() => window.open(`/salon/${salon.id}`, '_blank')}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Support Tickets Tab */}
          {currentTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Tickets de support ({tickets.length})
              </Typography>
              
              <List>
                {tickets.map((ticket) => (
                  <Card key={ticket.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6">{ticket.subject}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {ticket.salonName} • {ticket.userEmail}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(ticket.createdAt).format('DD/MM/YYYY à HH:mm')}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <Chip 
                            onClick={() => {}}
                            label={getTicketStatusLabel(ticket.status)}
                            color={getTicketStatusColor(ticket.status)}
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {ticket.type} • {ticket.priority}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              startIcon={<ReplyIcon />}
                              onClick={() => setSelectedTicket(ticket)}
                            >
                              Répondre
                            </Button>
                            {ticket.status === 'open' && (
                              <Button
                                size="small"
                                onClick={() => updateTicketStatus(ticket.id, 'resolved')}
                              >
                                Résoudre
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                      
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        {ticket.description.substring(0, 200)}
                        {ticket.description.length > 200 && '...'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </List>
            </Box>
          )}

          {/* Statistics Tab */}
          {currentTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Répartition des abonnements
                    </Typography>
                    <Box>
                      <Typography variant="body2">
                        Essais gratuits: {stats.trialSalons}
                      </Typography>
                      <Typography variant="body2">
                        Abonnements payants: {stats.paidSalons}
                      </Typography>
                      <Typography variant="body2">
                        Revenus mensuels estimés: {stats.totalRevenue}€
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Support
                    </Typography>
                    <Box>
                      <Typography variant="body2">
                        Tickets ouverts: {tickets.filter(t => t.status === 'open').length}
                      </Typography>
                      <Typography variant="body2">
                        Tickets en cours: {tickets.filter(t => t.status === 'in_progress').length}
                      </Typography>
                      <Typography variant="body2">
                        Tickets résolus: {tickets.filter(t => t.status === 'resolved').length}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>

      {/* Reply to Ticket Dialog */}
      <Dialog 
        open={!!selectedTicket} 
        onClose={() => setSelectedTicket(null)} 
        maxWidth="md" 
        fullWidth
      >
        {selectedTicket && (
          <>
            <DialogTitle>
              Répondre au ticket: {selectedTicket.subject}
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  De: {selectedTicket.salonName} ({selectedTicket.userEmail})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {selectedTicket.type} • Priorité: {selectedTicket.priority}
                </Typography>
              </Box>
              
              <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                <Typography variant="body1">
                  {selectedTicket.description}
                </Typography>
              </Paper>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Votre réponse"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Tapez votre réponse ici..."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedTicket(null)}>
                Annuler
              </Button>
              <Button 
                onClick={replyToTicket} 
                variant="contained" 
                disabled={loading || !replyMessage.trim()}
              >
                {loading ? 'Envoi...' : 'Envoyer la réponse'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;